import { getToken } from '@helpscout/hsds-tokens/getToken'
import type { GetTokenInput } from '@helpscout/hsds-tokens/getToken'
import get from 'lodash.get'
import styled, { css } from 'styled-components'

import Icon from 'hsds/components/icon'
import Spinner from 'hsds/components/spinner'
import { variableFontSize } from 'hsds/utils/fonts'
import { focusRing, focusShadowWithInset } from 'hsds/utils/mixins'

import config, { ButtonSizeConfig } from './Button.config'
import type { ButtonThemeProp } from './Button.types'

// Brand tokens are injected at runtime via ThemeProvider, not in the static
// token map, so we use this helper to assert the paths.
const brandToken = (path: string) => getToken(path as GetTokenInput)

function dashToCamelCase(myStr: string) {
  return myStr.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase()
  })
}

export const ButtonUI = styled.button`
  --hsds-button-main-color: ${getToken(`button.color.blue.main`)};
  --hsds-button-background-color: ${getToken(`button.color.blue.main`)};
  --hsds-button-hover-background-color: ${getToken(`button.color.blue.hover`)};
  --hsds-button-border-color: ${getToken(`button.color.blue.main`)};
  --hsds-button-hover-border-color: ${getToken(`button.color.blue.hover`)};
  --hsds-button-color: ${getToken(`button.color.blue.text`)};
  --hsds-button-hover-color: ${getToken(`button.color.blue.text`)};

  --hsds-button-text-decoration: none;
  --hsds-button-hover-text-decoration: none;

  --hsds-button-padding: var(--hsds-button-internal-padding, 0px);
  --hsds-button-rounded-padding: 20px;
  --hsds-button-min-width: var(--hsds-button-internal-min-width, initial);
  --hsds-button-height: var(
    --hsds-button-internal-height,
    ${config.size.lg.height}
  );
  --hsds-button-font-weight: ${config.fontWeight};
  --hsds-button-border-radius: ${config.borderRadius};

  --hsds-button-shortcut-background-color: ${getToken(
    'button.color.blue.shortcut.background'
  )};
  --hsds-button-shortcut-hover-background-color: ${getToken(
    'button.color.blue.shortcut.backgroundHover'
  )};

  --hsds-button-disabled-main-color: ${getToken(`button.color.disabled.main`)};
  --hsds-button-disabled-color: ${getToken(`button.color.disabled.text`)};
  --hsds-button-disabled-background-color: ${getToken(
    `button.color.disabled.outlined.background`
  )};
  --hsds-button-disabled-shortcut-background-color: ${getToken(
    `button.color.disabled.shortcut.background`
  )};
  --hsds-button-disabled-shortcut-color: ${getToken(
    `button.color.disabled.shortcut.text`
  )};
  --hsds-button-disabled-outlined-background-color: ${getToken(
    `button.color.disabled.outlined.background`
  )};
  --hsds-button-disabled-outlined-shortcut-background-color: ${getToken(
    `button.color.disabled.outlined.shortcut.background`
  )};
  --hsds-button-disabled-outlined-shortcut-color: ${getToken(
    `button.color.disabled.outlined.shortcut.text`
  )};
  --hsds-button-disabled-outlined-border-color: ${getToken(
    `button.color.disabled.outlined.border`
  )};
  --hsds-button-disabled-outlined-color: ${getToken(
    `button.color.disabled.outlined.text`
  )};

  --hsds-button-shortcut-size: 24px;
  --hsds-button-transition: none;

  ${focusRing};
  --hsds-focus-ring-offset: -3px;
  --hsds-focus-ring-radius: var(--hsds-button-border-radius);

  align-items: center;
  appearance: none;
  background: var(--hsds-button-background-color);
  border-radius: var(--hsds-button-border-radius);
  border: 1px solid var(--hsds-button-border-color);
  color: var(--hsds-button-color);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--hsds-global-font-family);
  font-weight: var(--hsds-button-font-weight);
  height: var(--hsds-button-height);
  justify-content: center;
  line-height: 1;
  min-width: var(--hsds-button-min-width);
  outline: none;
  padding: 0 var(--hsds-button-padding-right, var(--hsds-button-padding)) 0
    var(--hsds-button-padding-left, var(--hsds-button-padding));
  position: relative;
  text-align: center;
  transition: var(--hsds-button-transition);
  text-decoration: var(--hsds-button-text-decoration);
  word-break: keep-all;
  white-space: nowrap;

  .c-Button--shortcut {
    background: var(--hsds-button-shortcut-background-color);
  }

  .c-Button__Text + .c-Button__Text {
    margin-left: 0.25em;
  }

  &:hover,
  &:active,
  &:focus {
    outline: none;
    text-decoration: none;
  }

  &:focus {
    color: var(--hsds-button-color);
    text-decoration: none;
    text-decoration: var(--hsds-button-text-decoration);
  }

  &:hover,
  &.is-active,
  &:active {
    background: var(--hsds-button-hover-background-color);
    border-color: var(--hsds-button-hover-border-color);
    color: var(--hsds-button-hover-color);
    text-decoration: var(--hsds-button-hover-text-decoration);

    .c-Button--shortcut {
      background: var(--hsds-button-shortcut-hover-background-color);
    }
  }

  &.is-disabled,
  &[disabled] {
    pointer-events: none;
    user-select: none;
    background: var(--hsds-button-disabled-main-color) !important;
    border-color: var(--hsds-button-disabled-main-color) !important;
    color: var(--hsds-button-disabled-color, white) !important;

    &.is-style-outlined {
      background: var(
        --hsds-button-disabled-outlined-background-color
      ) !important;
      border-color: var(
        --hsds-button-disabled-outlined-border-color
      ) !important;
      color: var(--hsds-button-disabled-outlined-color) !important;
    }
    &.is-style-link {
      background: transparent !important;
      border-color: transparent !important;
      color: var(--hsds-button-disabled-outlined-color) !important;
    }

    &:before {
      display: none;
    }

    .is-disabled &,
    [disabled] & {
      --hsds-button-shortcut-background-color: var(
        --hsds-button-disabled-shortcut-background-color,
        var(--hsds-button-disabled-outlined-shortcut-background-color)
      );
      --hsds-button-shortcut-color: var(
        --hsds-button-disabled-shortcut-color,
        var(--hsds-button-disabled-outlined-shortcut-color)
      );
      background: var(--hsds-button-shortcut-background-color);
      color: var(--hsds-button-shortcut-color);
    }
  }

  &.is-aria-disabled {
    pointer-events: auto;
    cursor: default;

    /* Soft-disabled buttons stay focusable, so restore the focus ring the
       disabled block hides. */
    &:before {
      display: block;
    }
  }

  .c-ControlGroupItem & {
    --hsds-focus-ring-shadow: ${focusShadowWithInset};
  }

  ${makeButtonSizeStyles('xxl', config.size.xxl)};
  ${makeButtonSizeStyles('xl', config.size.xl)};
  ${makeButtonSizeStyles('lg', config.size.lg)};
  ${makeButtonSizeStyles('md', config.size.md)};
  ${makeButtonSizeStyles('sm', config.size.sm)};
  ${makeButtonSizeStyles('xs', config.size.xs)};
  ${makeButtonSizeStyles('xxs', config.size.xxs)};

  ${makeButtonColorStyles('blue')};
  ${makeButtonColorStyles('green')};
  ${makeButtonColorStyles('red')};
  ${makeButtonColorStyles('grey')};
  ${makeButtonColorStyles('yellow')};
  ${makeButtonColorStyles('purple')};
  ${makeButtonColorStyles('green-light', { link: false })};
  ${makeButtonColorStyles('grey-light', { link: false })};
  ${makeButtonColorStyles('blue-light', { link: false })};
  ${makeButtonColorStyles('red-accent', { link: false })};
  ${makeButtonColorStyles('blue-accent', { link: false })};
  ${makeButtonColorStyles('green-accent', { link: false })};
  ${makeButtonColorStyles('ai-purple', { outline: false, link: false })};

  // Brand tokens are injected at runtime via ThemeProvider, not in the
  // static token map, so we use brandToken() to assert the paths.
  &.is-color-brand {
    --hsds-button-main-color: ${brandToken('button.color.brand.main')};
    --hsds-button-background-color: ${brandToken('button.color.brand.main')};
    --hsds-button-hover-background-color: ${brandToken(
      'button.color.brand.hover'
    )};
    --hsds-button-border-color: ${brandToken('button.color.brand.main')};
    --hsds-button-hover-border-color: ${brandToken('button.color.brand.hover')};
    --hsds-button-color: ${brandToken('button.color.brand.text')};
    --hsds-button-hover-color: ${brandToken('button.color.brand.text')};

    // Brand has no dedicated shortcut token (brand colors are populated at
    // runtime by mapBrandColorToConfig), so consume the shortcut background it
    // derives from the brand palette; the brand text color sits on top.
    // Without this the badge falls through to the base blue shortcut.
    --hsds-button-shortcut-background-color: ${brandToken(
      'button.color.brand.shortcut.background'
    )};
    --hsds-button-shortcut-hover-background-color: ${brandToken(
      'button.color.brand.shortcut.background'
    )};
    --hsds-button-shortcut-color: ${brandToken('button.color.brand.text')};

    --hsds-button-outlined-background-color: white;
    --hsds-button-outlined-hover-background-color: ${brandToken(
      'button.color.brand.outlined.hover'
    )};
    --hsds-button-outlined-color: ${brandToken(
      'button.color.brand.outlined.text'
    )};
    --hsds-button-outlined-hover-color: ${brandToken(
      'button.color.brand.outlined.textHover'
    )}; //fallback textColor
    --hsds-button-outlined-border-color: ${brandToken(
      'button.color.brand.outlined.border'
    )}; // fallback mainColor
    --hsds-button-outlined-hover-border-color: ${brandToken(
      'button.color.brand.outlined.borderHover'
    )};

    --hsds-button-linked-color: ${brandToken(
      'button.color.brand.outlined.text'
    )};
    --hsds-button-linked-hover-color: ${brandToken(
      'button.color.brand.outlined.textHover'
    )};
  }

  // variant overwrites
  &.is-rounded {
    --hsds-button-border-radius: ${getToken('radius.circle')} !important;
    --hsds-button-internal-min-width: 0px;
    --hsds-button-padding-left: calc(
      min(var(--hsds-button-rounded-padding), var(--hsds-button-padding))
    );
    --hsds-button-padding-right: calc(
      min(var(--hsds-button-rounded-padding), var(--hsds-button-padding))
    );
  }

  &.is-style-outlined {
    --hsds-button-background-color: var(
      --hsds-button-outlined-background-color
    );
    --hsds-button-hover-background-color: var(
      --hsds-button-outlined-hover-background-color
    );
    --hsds-button-color: var(--hsds-button-outlined-color);
    --hsds-button-hover-color: var(
      --hsds-button-outlined-hover-color
    ); //fallback textColor
    --hsds-button-border-color: var(
      --hsds-button-outlined-border-color
    ); // fallback mainColor
    --hsds-button-hover-border-color: var(
      --hsds-button-outlined-hover-border-color
    );

    --hsds-button-shortcut-background-color: var(
      --hsds-button-shortcut-outlined-background-color
    );
    --hsds-button-shortcut-hover-background-color: var(
      --hsds-button-shortcut-outlined-hover-background-color
    );
  }

  &.is-style-link {
    --hsds-button-internal-min-width: 0;
    --hsds-button-internal-padding: 0;
    --hsds-button-background-color: transparent;
    --hsds-button-hover-background-color: transparent;
    --hsds-button-border-color: transparent;
    --hsds-button-hover-border-color: transparent;
    --hsds-button-hover-text-decoration: underline;
    --hsds-button-font-weight: 400;
    --hsds-button-color: var(--hsds-button-linked-color);
    --hsds-button-hover-color: var(--hsds-button-linked-hover-color);

    &.is-inlined {
      --hsds-button-height: auto;
    }
  }

  &.is-seamless {
    --hsds-button-color: var(--hsds-button-outlined-color);
    --hsds-button-hover-color: var(--hsds-button-outlined-seamless-hover-color);
    --hsds-button-background-color: transparent !important;
    --hsds-button-border-color: transparent !important;
    --hsds-button-hover-background-color: transparent !important;
    --hsds-button-hover-border-color: transparent !important;
  }

  // manage an icon present as children (either through prefix/suffix or a simple child element)
  &:not(.c-ButtonIcon, .c-ButtonCopy):has(> .c-Icon):not(
      :has(> .c-Icon:only-child)
    ) {
    //--hsds-button-padding-icon-large
    //--hsds-button-padding-icon-small
    --hsds-button-padding-left: var(--hsds-button-padding-icon-large);
    --hsds-button-padding-right: var(--hsds-button-padding-icon-large);
    --hsds-button-internal-min-width: 0px;
    gap: var(--hsds-button-icon-gap);
  }
  &&:has(> .c-Icon:only-child),
  &&:has(.c-IconButton__Attention) {
    --hsds-button-internal-padding: 0;
    --hsds-button-internal-min-width: var(--hsds-button-height);
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
  }

  &&:not(.c-ButtonIcon, .c-ButtonCopy):has(> .c-Icon:first-child):not(
      :has(> .c-Icon:only-child)
    ) {
    --hsds-button-padding-left: var(--hsds-button-padding-icon-small);
  }
  &&:not(.c-ButtonIcon, .c-ButtonCopy):has(> .c-Icon:last-child):not(
      :has(> .c-Icon:only-child)
    ) {
    --hsds-button-padding-right: var(--hsds-button-padding-icon-small);
  }
`

export const ShortcutUI = styled.span`
  background: var(--hsds-button-shortcut-background-color);
  transition: var(--hsds-button-transition);
  flex: 0 0 var(--hsds-button-shortcut-size);
  aspect-ratio: 1;
  display: flex;
  margin: 0 0 0 5px;
  border-radius: ${getToken('radius.small')};
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: var(--hsds-button-shortcut-font-size);
  color: var(--hsds-button-shortcut-color, currentColor);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
    'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans',
    'Helvetica Neue', Arial, sans-serif;

  .is-disabled &,
  [disabled] & {
    background: var(--hsds-button-disabled-shortcut-background-color);
    color: var(--hsds-button-disabled-shortcut-color);
  }

  .is-disabled.is-style-outlined &,
  [disabled].is-style-outlined & {
    background: var(--hsds-button-disabled-outlined-shortcut-background-color);
    color: var(--hsds-button-disabled-outlined-shortcut-color);
  }
`

export const LoadingWrapperUI = styled.span`
  align-items: inherit;
  display: inherit;
  justify-content: inherit;
  text-decoration: inherit;
  width: 100%;
  opacity: 0;
`

export const SpinnerUI = styled(Spinner)`
  color: inherit;
  margin: -6px 0 0 -6px;
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
`

export const PrefixIconUI = styled(Icon)`
  color: inherit;
`
export const SuffixIconUI = styled(Icon)`
  color: inherit;
`

function renderPropStyle(
  config: ButtonSizeConfig,
  prop: string,
  attribute: string,
  fallback: string | null = null
) {
  const attr = attribute || prop

  let value = get(config, prop) as string
  if (!value && fallback) value = get(config, fallback) as string

  if (!value) return ''

  return `
    ${attr}: ${value};
  `
}

function makeButtonColorStyles(
  color: string,
  { outline = true, link = true } = {}
) {
  const colorName = dashToCamelCase(color)
  // Color name is a known set of values (blue, red, green, etc.) but
  // TypeScript can't narrow the template literal, so we assert the paths.
  const token = (path: string) => getToken(path as GetTokenInput)
  return css`
    &.is-color-${color} {
      --hsds-button-main-color: ${token(`button.color.${colorName}.main`)};
      --hsds-button-background-color: ${token(
        `button.color.${colorName}.main`
      )};
      --hsds-button-hover-background-color: ${token(
        `button.color.${colorName}.hover`
      )};
      --hsds-button-border-color: ${token(`button.color.${colorName}.main`)};
      --hsds-button-hover-border-color: ${token(
        `button.color.${colorName}.hover`
      )};
      --hsds-button-color: ${token(`button.color.${colorName}.text`)};
      --hsds-button-hover-color: ${token(`button.color.${colorName}.text`)};

      --hsds-button-shortcut-background-color: ${token(
        `button.color.${colorName}.shortcut.background`
      )};
      --hsds-button-shortcut-hover-background-color: ${token(
        `button.color.${colorName}.shortcut.backgroundHover`
      )};
      ${outline
        ? css`
            --hsds-button-outlined-background-color: ${token(
              `button.color.${colorName}.outlined.background`
            )};
            --hsds-button-outlined-hover-background-color: ${token(
              `button.color.${colorName}.outlined.hover`
            )};
            --hsds-button-outlined-color: ${token(
              `button.color.${colorName}.outlined.text`
            )};
            --hsds-button-outlined-hover-color: ${token(
              `button.color.${colorName}.outlined.textHover`
            )}; //fallback textColor
            --hsds-button-outlined-border-color: ${token(
              `button.color.${colorName}.outlined.border`
            )}; // fallback mainColor
            --hsds-button-outlined-hover-border-color: ${token(
              `button.color.${colorName}.outlined.borderHover`
            )};

            --hsds-button-shortcut-outlined-background-color: ${token(
              `button.color.${colorName}.outlined.shortcut.background`
            )};
            --hsds-button-shortcut-outlined-hover-background-color: ${token(
              `button.color.${colorName}.outlined.shortcut.backgroundHover`
            )};

            --hsds-button-outlined-seamless-hover-color: ${token(
              `button.color.${colorName}.outlined.textSeamlessHover`
            )};
          `
        : ''}
      ${link
        ? css`
            --hsds-button-linked-color: ${token(
              `button.color.${colorName}.linked.text`
            )};
            --hsds-button-linked-hover-color: ${token(
              `button.color.${colorName}.linked.textHover`
            )};
          `
        : ''}
    }
  `
}

function makeButtonSizeStyles(size: string, config: ButtonSizeConfig) {
  return css`
    &.is-size-${size} {
      ${variableFontSize({ fontSize: config.fontSize })};
      ${renderPropStyle(config, 'height', '--hsds-button-internal-height')};
      ${renderPropStyle(config, 'padding', '--hsds-button-internal-padding')};
      ${renderPropStyle(config, 'shortcutSize', '--hsds-button-shortcut-size')};
      ${renderPropStyle(
        config,
        'shortcutFontSize',
        '--hsds-button-shortcut-font-size'
      )};
      ${renderPropStyle(config, 'iconGap', '--hsds-button-icon-gap')};
      ${renderPropStyle(
        config,
        'paddingIconLarge',
        '--hsds-button-padding-icon-large'
      )};
      ${renderPropStyle(
        config,
        'paddingIconSmall',
        '--hsds-button-padding-icon-small'
      )};
      ${renderPropStyle(config, 'borderRadius', '--hsds-button-border-radius')};
      ${renderPropStyle(
        config,
        'roundedPadding',
        '--hsds-button-rounded-padding'
      )};
    }
  `
}

export function mapBrandColorToConfig(theme?: ButtonThemeProp) {
  if (!theme) return {}

  const mainColor = theme.backgroundColorUI || theme.brandColor
  const textColor = theme.textColorInteractive || theme.brandTextColor
  const hoverColor = theme.backgroundColorUIHover
  // Brand has no static shortcut token, so use the dedicated shortcut shade
  // makeBrandColors derives (a step past hover — darker for light brands,
  // lighter for dark ones), falling back to the active/hover shade.
  const shortcutBackground =
    theme.backgroundColorShortcutUI ||
    theme.backgroundColorUIActive ||
    hoverColor

  // Outlined and linked brand buttons sit on white, where a light brand can't
  // carry its own text or border; use its derived dark text shade instead.
  const isLightBrand =
    theme.colorShade === 'lightest' || theme.colorShade === 'light'
  const outlinedAccent = isLightBrand ? textColor : mainColor

  return {
    '--hsds-token-button-color-brand-main': mainColor,
    '--hsds-token-button-color-brand-hover': hoverColor,
    '--hsds-token-button-color-brand-text': textColor,
    '--hsds-token-button-color-brand-shortcut-background': shortcutBackground,
    '--hsds-token-button-color-brand-outlined-border': outlinedAccent,
    '--hsds-token-button-color-brand-outlined-borderHover': isLightBrand
      ? outlinedAccent
      : hoverColor,
    '--hsds-token-button-color-brand-outlined-text': outlinedAccent,
    '--hsds-token-button-color-brand-outlined-textHover': outlinedAccent,
    '--hsds-token-button-color-brand-outlined-hover':
      mainColor && `color-mix(in srgb, ${mainColor} 10%, white)`,
  }
}
